import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { clearSession, getUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { 
  Home, Search, TrendingUp, Users, Bot, FileText, 
  Layers, Zap, Building, Settings, Briefcase, Bell, 
  Calculator, GripVertical, Eye, EyeOff, ChevronDown,
  Menu, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

// Import Phone icon for Voice AI
import { Phone, BarChart3 } from 'lucide-react';

// Icon mapping - FIX for React 19 compatibility
const ICON_MAP = {
  'Zap': Zap,
  'Search': Search,
  'Layers': Layers,
  'TrendingUp': TrendingUp,
  'FileText': FileText,
  'Users': Users,
  'Bot': Bot,
  'Phone': Phone,
  'BarChart3': BarChart3,
  'Briefcase': Briefcase,
  'Calculator': Calculator,
  'Bell': Bell,
  'Settings': Settings,
};

const DEFAULT_NAV_ITEMS = [
  { id: 'command', path: '/', iconName: 'Zap', label: 'Operations Hub', enabled: true },
  { id: 'search', path: '/search', iconName: 'Search', label: 'Market Intelligence', enabled: true },
  { id: 'comps', path: '/comps', iconName: 'BarChart3', label: 'Legion Valuation', enabled: true },
  { id: 'deals', path: '/deals', iconName: 'Layers', label: 'Deal Pipelines', enabled: true },
  { id: 'underwriting', path: '/underwriting', iconName: 'TrendingUp', label: 'Underwriting', enabled: true },
  { id: 'offers', path: '/contracts', iconName: 'FileText', label: 'Offers', enabled: true },
  { id: 'crm', path: '/crm', iconName: 'Users', label: 'CRM', enabled: true },
  { id: 'legion', path: '/legion', iconName: 'Bot', label: 'Configure Legion Bots', enabled: true },
  { id: 'voice-ai', path: '/voice-ai', iconName: 'Phone', label: 'Configure Legion Voice Agents', enabled: true },
  { id: 'portfolio', path: '/portfolio', iconName: 'Briefcase', label: 'Portfolio', enabled: true },
  { id: 'accounting', path: '/accounting', iconName: 'Calculator', label: 'Accounting', enabled: true },
  { id: 'notifications', path: '/notifications', iconName: 'Bell', label: 'Notifications', enabled: true },
  { id: 'admin', path: '/admin', iconName: 'Settings', label: 'Admin', enabled: true },
];

const Layout = () => {
  const location = useLocation();
  const [navItems, setNavItems] = useState(() => {
    const saved = localStorage.getItem('sidebar_config');
    if (saved) {
      const savedItems = JSON.parse(saved);
      // Merge saved config with new default items
      const savedIds = savedItems.map(item => item.id);
      const newItems = DEFAULT_NAV_ITEMS.filter(item => !savedIds.includes(item.id));
      return [...savedItems, ...newItems];
    }
    return DEFAULT_NAV_ITEMS;
  });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('sidebar_config', JSON.stringify(navItems));
  }, [navItems]);

  const toggleNavItem = (id) => {
    setNavItems(prev => prev.map(item => 
      item.id === id ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newItems = [...navItems];
    const draggedItemContent = newItems[draggedItem];
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedItemContent);
    setNavItems(newItems);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const resetToDefault = () => {
    setNavItems(DEFAULT_NAV_ITEMS);
    localStorage.removeItem('sidebar_config');
    toast.success('Reset to default!');
  };

  const saveLayout = () => {
    localStorage.setItem('sidebar_config', JSON.stringify(navItems));
    toast.success('Layout saved!');
    setCustomizeOpen(false);
  };

  const visibleItems = navItems.filter(item => item.enabled);

  return (
    <div className="min-h-screen flex" data-testid="main-layout">
      {/* Mobile Menu Button */}
      <button 
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card border rounded-md"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transform transition-transform lg:transform-none ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`} 
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-border">
          <h1 className="text-2xl font-black tracking-tight text-primary" data-testid="app-logo">REIPR</h1>
          <p className="text-xs text-muted-foreground mt-1">Real Estate Intelligence</p>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto" data-testid="main-navigation">
          <ul className="space-y-1">
            {visibleItems.map((item) => {
              const IconComponent = ICON_MAP[item.iconName];
              const isActive = location.pathname === item.path || 
                (item.path === '/deals' && location.pathname.startsWith('/deals'));
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${item.id}`}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-all ${
                      isActive
                        ? 'bg-primary text-primary-foreground glow-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                  >
                    {IconComponent && <IconComponent size={18} />}
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Signed-in user + sign out */}
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate" title={getUser()?.email || ''}>
              {getUser()?.email || 'Signed in'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="sign-out"
              onClick={() => {
                clearSession();
                window.location.replace('/login');
              }}
            >
              Sign out
            </Button>
          </div>
        </div>

        {/* Customize Button */}
        <div className="p-4 border-t border-border">
          <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <Settings className="h-4 w-4 mr-2" /> Customize Sidebar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Customize Sidebar</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 pt-4 max-h-96 overflow-y-auto">
                <p className="text-sm text-muted-foreground mb-4">
                  Drag to reorder, toggle to show/hide
                </p>
                {navItems.map((item, index) => {
                  const IconComponent = ICON_MAP[item.iconName];
                  return (
                    <div 
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center justify-between p-3 border rounded-lg cursor-move ${
                        draggedItem === index ? 'opacity-50 border-primary' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        <span className="font-medium text-sm">{item.label}</span>
                      </div>
                      <Switch 
                        checked={item.enabled} 
                        onCheckedChange={() => toggleNavItem(item.id)} 
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={resetToDefault}>
                  Reset to Default
                </Button>
                <Button className="flex-1" onClick={saveLayout}>
                  <Check className="h-4 w-4 mr-2" /> Save Layout
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <div className="text-xs text-muted-foreground mt-4">
            <p>© 2025 REIPR</p>
            <p className="mt-1">A product of KlearWater LLC.</p>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="flex-1 overflow-auto lg:ml-0" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
